import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { Charge, TransactionItem } from 'apollo/queries/autogenerate/schemas';
import GradientProgress from 'shared/modules/common/components/molecules/GradientProgress';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import {
  getCurrentInstallmentIndex,
  getTotalInstallment,
} from 'shared/modules/common/utils/charge-utils';
import { formatYearMonthDate, formatToCurrency } from 'shared/utils/japanese';
import AppleTradeInBanner from 'web/modules/common/components/molecules/AppleTradeInBanner';
import CreditTotal from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/CreditTotal';
import DescriptionItem from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/DescriptionItem';

type Props = {
  charge: Charge;
  transactionItem: TransactionItem;
};

const TradeInInstallmentProgress = ({
  installmentNumber,
  numberOfInstallments,
}: {
  installmentNumber: number;
  numberOfInstallments: number;
}) => (
  <>
    <View style={styles.header}>
      <Text style={styles.headerText}>現在のお支払い状況</Text>
    </View>
    <GradientProgress progress={installmentNumber / numberOfInstallments} />
    <View style={styles.description}>
      <Text style={styles.titleText}>
        {installmentNumber} / {numberOfInstallments}回
      </Text>
    </View>
  </>
);

// If the tradeInValue (the total, not the installment amount) is > 3000, the amount is not shown.
const TradeInShortSummary = ({ tradeInValue }: { tradeInValue: number }) => (
  <View style={styles.subtitle}>
    <Text style={styles.detailLabel}>下取り調整差額</Text>
    {tradeInValue && tradeInValue < 3000 ? (
      <Text style={styles.detailValue}>{formatToCurrency(tradeInValue)}</Text>
    ) : null}
  </View>
);

// Trade-ins in this screen are always online trade-ins.
// Trade-ins done in store are treated as down payment.
export default function AppleTradeInDetails(props: Props) {
  const charge = props.charge;
  const item = props.transactionItem;

  const tradeInValue = item.unitPrice;
  const isTradeInAmountAbove3000 = tradeInValue > 3000;
  const installmentAmount = item?.monthlyAmount ?? 0;
  const installmentNumber = getCurrentInstallmentIndex(charge);
  const numberOfInstallments = getTotalInstallment(charge);

  if (!numberOfInstallments || installmentNumber === -1) {
    return null;
  }

  return (
    <View style={styles.container}>
      {/** progress bar */}
      {isTradeInAmountAbove3000 ? (
        <TradeInInstallmentProgress
          installmentNumber={installmentNumber}
          numberOfInstallments={numberOfInstallments}
        />
      ) : null}

      {/** summary. */}
      <TradeInShortSummary tradeInValue={tradeInValue} />

      {/** detail. */}
      {isTradeInAmountAbove3000 ? (
        <>
          {/** Trade in installment amount */}
          <DescriptionItem
            title="分割支払い金"
            description={`${formatToCurrency(
              installmentAmount
            )} / ${numberOfInstallments}月`}
          />
          {/** interest rate */}
          <DescriptionItem
            title="分割金利*"
            description={`0% / ${numberOfInstallments}ヶ月`}
            note="*口座振替、銀行振り込みに限ります"
          />
          {/** total */}
          <CreditTotal
            total={formatToCurrency(tradeInValue)}
            containerStyle={styles.total}
          />
        </>
      ) : null}

      {/** trade in banner */}
      <AppleTradeInBanner
        price={tradeInValue}
        purchasedAt={formatYearMonthDate(charge.payment?.createdAt)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 32,
    paddingTop: 16,
    paddingBottom: 32,
    backgroundColor: 'white',
  },
  header: {
    paddingTop: 16,
    paddingBottom: 24,
  },
  description: {
    paddingVertical: 12,
  },
  subtitle: {
    paddingTop: 12,
    paddingBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerText: {
    ...typography.title02,
    fontWeight: '700',
    fontSize: 20,
    lineHeight: 24,
    color: colors.blackMedium,
  },
  titleText: {
    ...typography.title03,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 24,
    color: colors.blackMedium,
  },
  detailLabel: {
    ...typography.caption01,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 16,
    color: colors.blackMedium,
  },
  detailValue: {
    ...typography.caption02,
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 16,
    color: colors.teal,
  },
  total: {
    paddingTop: 16,
  },
});
